/**
 * Minimal PDF -> image processor for layout uploads.
 * Loads pdf.js from a CDN at runtime (no npm dependency required), renders the
 * first page to a high-resolution PNG data URL. Ported/condensed from
 * front/src/features/pdf-handler/pdfProcessor.ts.
 */

const PDFJS_VERSION = "3.11.174";
const PDFJS_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

export interface PDFProcessingResult {
  imageDataUrl: string;
  numPages: number;
}

export class PDFProcessor {
  private pdfjsLib: any = null;

  private async loadPDFJS(): Promise<any> {
    if (this.pdfjsLib) return this.pdfjsLib;
    if (typeof window === "undefined") {
      throw new Error("Not in a browser environment");
    }

    const existing = (window as any).pdfjsLib;
    if (existing) {
      existing.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      this.pdfjsLib = existing;
      return existing;
    }

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = PDFJS_SRC;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load PDF.js from CDN"));
      document.head.appendChild(script);
    });

    const pdfjsLib = (window as any).pdfjsLib;
    if (!pdfjsLib) throw new Error("PDF.js failed to initialise");
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    this.pdfjsLib = pdfjsLib;
    return pdfjsLib;
  }

  async getPDFInfo(file: File): Promise<{ numPages: number }> {
    const pdfjsLib = await this.loadPDFJS();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    return { numPages: pdf.numPages };
  }

  async processPDF(file: File): Promise<PDFProcessingResult> {
    const pdfjsLib = await this.loadPDFJS();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;

    const firstPage = await pdf.getPage(1);
    const viewport = firstPage.getViewport({ scale: 2.0 });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to create canvas context");
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await firstPage.render({ canvasContext: context, viewport }).promise;

    return {
      imageDataUrl: canvas.toDataURL("image/png"),
      numPages: pdf.numPages,
    };
  }
}
